/**
 * לקוחות ייצור — the organisations we buy and manufacture *for*.
 *
 * ## Why this is not `Customers`
 *
 * `Customers` (הנהלת חשבונות → לקוחות) models the people who buy from us: a subscription, a
 * collection status, a payment method, a debt. A production customer is the opposite direction of
 * trade — an organisation that asks *us* to have something made, and whose row holds an invoicing
 * address and one contact person and nothing else.
 *
 * Merging them would put `collection`, `treatmentStatus` and `paymentMethod` on a row where all
 * three are meaningless, and would make "כמה לקוחות יש לנו" a question with two answers. The
 * requester asked for the separation by name. Keep it.
 *
 * The next module — הזמנות עבודה — opens an order *for* one of these, and the plan is that these
 * organisations eventually log in and open their own. That is why the row exists before anything
 * reads it in anger.
 *
 * No React, no MUI, no browser globals.
 */

import { PhOption, phOptions } from './productHub.core';

export const PRODUCTION_CUSTOMER_STATUSES = ['active', 'inactive'] as const;
export type ProductionCustomerStatus = (typeof PRODUCTION_CUSTOMER_STATUSES)[number];

export const PRODUCTION_CUSTOMER_STATUS_LABELS: Record<ProductionCustomerStatus, string> = {
  active: 'פעיל',
  inactive: 'לא פעיל'
};
export const PRODUCTION_CUSTOMER_STATUS_OPTIONS: PhOption<ProductionCustomerStatus>[] = phOptions(
  PRODUCTION_CUSTOMER_STATUSES,
  PRODUCTION_CUSTOMER_STATUS_LABELS
);

/**
 * סוג לקוח — how the organisation relates to us, which is not the same axis as `status`.
 *
 * `status` says whether we work with them *now*; this says *what they are* — a Chabad house or
 * institution of our own, a joint venture, an outside organisation that comes back every season, or
 * one that ordered once. The distinction is what the requester asked for by name, and it is the
 * split that decides pricing conversations and which orders count as internal work.
 *
 * Codes are stable and English, labels are the requester's own Hebrew wording. Adding a type is one
 * entry here — the DB column is a STRING, so it is never an `ALTER TYPE`.
 */
export const PRODUCTION_CUSTOMER_TYPES = [
  'internal',
  'partnership',
  'external_regular',
  'external_occasional'
] as const;
export type ProductionCustomerType = (typeof PRODUCTION_CUSTOMER_TYPES)[number];

export const PRODUCTION_CUSTOMER_TYPE_LABELS: Record<ProductionCustomerType, string> = {
  internal: 'מרכז לעזרי שליחות',
  partnership: 'לקוח שותפות',
  external_regular: 'לקוח חיצוני קבוע',
  external_occasional: 'לקוח חיצוני מזדמן'
};
export const PRODUCTION_CUSTOMER_TYPE_OPTIONS: PhOption<ProductionCustomerType>[] = phOptions(
  PRODUCTION_CUSTOMER_TYPES,
  PRODUCTION_CUSTOMER_TYPE_LABELS
);

export interface ProductionCustomerDto {
  id: number;
  organizationName: string;
  /** `null` on every row created before the field existed — "nobody has said yet", not a default. */
  customerType: ProductionCustomerType | null;
  /** Free text on purpose: an invoicing block is a name, an ID number, an address and a VAT line, and every customer writes it differently. */
  invoiceDetails: string | null;
  contactName: string | null;
  /** Stored normalised by `normalizePhone` from `@malash-office/phone-utils`, displayed by `displayPhoneNumber`. */
  contactPhone: string | null;
  notes: string | null;
  status: ProductionCustomerStatus;
  /** How many planning projects point at this customer — the list column, and what blocks archiving. */
  projectCount: number;
  /**
   * The card this organisation has in הנהלת חשבונות → לקוחות (`/finance/customers/:id`).
   *
   * Every production customer is filed there too, under `CUSTOMER_TYPE_PRODUCTION` — asked for by
   * name. `null` means no card: either the row has no phone (the phone is the identity a card is
   * matched by, and it is required on creation), or the phone matched **several** existing cards, in
   * which case the module links to none of them rather than guessing which one is the organisation.
   */
  customerId: number | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertProductionCustomerBody {
  organizationName: string;
  customerType?: ProductionCustomerType | null;
  invoiceDetails?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  notes?: string | null;
  status?: ProductionCustomerStatus;
  /**
   * The organisation's other contact people, linked in the same save as the card. Each one signs
   * into the portal with the same role as the card's own contact. The card's contact is
   * `contactName`/`contactPhone` and is never listed here.
   */
  additionalContacts?: CreateProductionCustomerPortalUserBody[];
  /** Edit only: contacts already linked whose access is turned on or off by this save. */
  contactStatuses?: ProductionCustomerContactStatusChange[];
}

export interface ProductionCustomerContactStatusChange {
  userId: number;
  status: ProductionCustomerPortalUserDto['status'];
}

export interface ProductionCustomerListResponse {
  rows: ProductionCustomerDto[];
  count: number;
}

/** The picker on the new-project drawer, for `external` projects. Name only — nothing else fits a `Select`. */
export interface ProductionCustomerOption {
  id: number;
  organizationName: string;
}

/* ── the people who log in ────────────────────────────────────────────────────────────────── */

/**
 * A contact person who can sign into the portal on the organisation's behalf.
 *
 * A join row (`ProductionCustomerUsers`) and not a column, because an organisation has more than
 * one contact — the model's header says so, and a single `userId` is exactly where that breaks.
 * The identity itself is an ordinary `Users` row with the "לקוח חיצוני" role, so the login is the
 * same phone-OTP login everyone else uses and there is no second credential to maintain.
 *
 * `isPrimary` marks the one provisioned from `contactPhone` when the card was opened. It carries no
 * privilege — it is there so the card can say which row came from the card itself.
 */
export interface ProductionCustomerPortalUserDto {
  userId: number;
  fullName: string;
  /** Local digits, the form `Users.phone` is matched on at login (`0501234567`). */
  phone: string;
  isPrimary: boolean;
  /** `active` may sign in; `disabled` may not, and the link survives so the history stays readable. */
  status: 'active' | 'disabled';
  /**
   * True when the phone already belonged to somebody in the system — a colleague who is also this
   * organisation's contact. We link them and **never touch their role**, so the card says as much.
   */
  isExistingSystemUser: boolean;
  createdAt: string;
}

export interface CreateProductionCustomerPortalUserBody {
  fullName: string;
  phone: string;
}
